/* Encapsulates all bulk-action handlers for the Manage Users table.
 * Returns ready-to-bind onClick handlers + busy state. Keeps ManageUsersCard
 * focused on layout instead of mutation logic.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function useBulkUserActions({ selectedIds, clearSelection, reload }) {
  const [bulkBusy, setBulkBusy] = useState(false);

  const runBulk = async (label, fn, { confirm } = {}) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (confirm && !window.confirm(confirm.replace("{n}", ids.length))) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    const errors = [];
    for (const id of ids) {
      try {
        await fn(id);
        ok += 1;
      } catch (err) {
        fail += 1;
        const d = err.response?.data?.detail;
        errors.push(typeof d === "string" ? d : "Error");
      }
    }
    setBulkBusy(false);
    clearSelection();
    await reload();
    if (fail === 0) toast.success(`${label}: ${ok} user${ok === 1 ? "" : "s"} updated`);
    else if (ok === 0) toast.error(`${label} failed: ${errors[0] || "unknown error"}`);
    else toast.warning(`${label}: ${ok} updated, ${fail} failed`);
  };

  return {
    bulkBusy,
    bulkDeactivate: () => runBulk(
      "Bulk deactivate",
      (id) => api.patch(`/admin/users/${id}`, { is_active: false }),
      { confirm: "Deactivate {n} selected user(s)? They won't be able to sign in until reactivated." },
    ),
    bulkReactivate: () => runBulk(
      "Bulk reactivate",
      (id) => api.patch(`/admin/users/${id}`, { is_active: true }),
    ),
    bulkSetRole: (role) => runBulk(
      `Bulk set role → ${role}`,
      (id) => api.patch(`/admin/users/${id}`, { role }),
      { confirm: `Change role of {n} selected user(s) to "${role}"?` +
        (role === "admin" ? "\n\nWARNING: admins can manage every user and delete data." : "") },
    ),
    bulkSetProgram: (program) => runBulk(
      `Bulk move → ${program === "men" ? "Men's" : "Women's"}`,
      (id) => api.patch(`/admin/users/${id}`, { program }),
      { confirm: `Move {n} selected user(s) to the ${program === "men" ? "Men's" : "Women's"} program?` },
    ),
    bulkDelete: () => runBulk(
      "Bulk delete",
      (id) => api.delete(`/admin/users/${id}`),
      { confirm: "PERMANENTLY delete {n} selected user(s) and all their journals, assessments, posts, and progress?\n\nThis cannot be undone." },
    ),
  };
}
